const STORAGE_KEY = "rm_attribution";
const MAX_LENGTH = 100;

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

const PARAMS: [keyof Attribution, string][] = [
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmContent", "utm_content"],
];

let current: Attribution = {};

const read = (): Attribution => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "{}") as Attribution;
  } catch {
    return {};
  }
};

const write = (value: Attribution) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Blocked storage: the in-memory copy still covers navigation inside the SPA
  }
};

/**
 * Remembers the UTM parameters of the first page that carries them, for this
 * browser session only. The campaign labels are not personal data, and a later
 * page without them (the visitor browsing on) must not overwrite them.
 */
export const captureAttribution = (search: string = window.location.search) => {
  current = { ...read(), ...current };

  if (Object.keys(current).length > 0) return;

  const query = new URLSearchParams(search);
  const found: Attribution = {};

  PARAMS.forEach(([key, name]) => {
    const value = query.get(name)?.trim();
    if (value) found[key] = value.slice(0, MAX_LENGTH);
  });

  if (Object.keys(found).length === 0) return;

  current = found;
  write(found);
};

/** Attribution to send with a booking, or an empty object when the visit had no UTM parameters. */
export const getAttribution = (): Attribution => ({ ...current, ...read() });

/** Test helper: forgets the in-memory copy. */
export const resetAttribution = () => {
  current = {};
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
